// complex.wgsl — complex-number arithmetic only (see viewport.wgsl for
// pixel-to-plane mapping and palette.wgsl for coloring). Concatenated ahead
// of every formula shader by src/util/pipeline.mjs: WGSL has no #include, so
// shared helpers are prepended at compile time instead of duplicated into
// each fractal's .wgsl file.
//
// This module declares no @group/@binding and no @compute entry point — it's
// never dispatched on its own, only used as a function library, so there are
// no bindings for a formula shader to offset around.

struct Complex { re: f32, im: f32 }

fn cAdd(a: Complex, b: Complex) -> Complex { return Complex(a.re + b.re, a.im + b.im); }
fn cSub(a: Complex, b: Complex) -> Complex { return Complex(a.re - b.re, a.im - b.im); }

fn cMul(a: Complex, b: Complex) -> Complex {
  return Complex(a.re * b.re - a.im * b.im, a.re * b.im + a.im * b.re);
}

// a / b = a * conj(b) / |b|^2 — the standard complex-division identity.
// Only Newton's shader needs this (dividing by f'(z)); the others never do.
fn cDiv(a: Complex, b: Complex) -> Complex {
  let denom = b.re * b.re + b.im * b.im;
  return Complex(
    (a.re * b.re + a.im * b.im) / denom,
    (a.im * b.re - a.re * b.im) / denom,
  );
}

fn cConj(a: Complex) -> Complex { return Complex(a.re, -a.im); }
fn cScale(a: Complex, s: f32) -> Complex { return Complex(a.re * s, a.im * s); }
fn cAbs2(a: Complex) -> f32 { return a.re * a.re + a.im * a.im; }

// Integer power by exponentiation-by-squaring. `n` is a runtime uniform value
// (the Multibrot degree), not a compile-time constant, so this can't unroll.
fn cPowN(a: Complex, n: u32) -> Complex {
  var result = Complex(1.0, 0.0);
  var base = a;
  var e = n;
  loop {
    if (e == 0u) { break; }
    if ((e & 1u) != 0u) { result = cMul(result, base); }
    base = cMul(base, base);
    e = e >> 1u;
  }
  return result;
}
